import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";

const BLANK = {
  name: "", description: "", category: "", resource_type: "course", url: "",
  affiliate_url: "", affiliate_network: "none", affiliate_program: "", image_url: "",
  price: "", goal_tags: [], stage_tags: [], domains: [], active: true,
  sponsored: false, sponsor_label: "", admin_notes: "", commission_note: "",
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const input =
  "w-full bg-transparent border border-white/10 rounded-sm px-3 py-2 text-sm outline-none focus:border-primary/60 transition-colors duration-200";

export default function AdminResources() {
  const [meta, setMeta] = useState(null);
  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(null);
  const [conv, setConv] = useState({ resource_id: "", affiliate_network: "amazon", amount: "", note: "" });

  const load = async () => {
    const [r, a] = await Promise.all([api.get("/admin/resources"), api.get("/admin/analytics")]);
    setResources(r.resources);
    setStats(a);
  };

  useEffect(() => {
    api.get("/admin/status").then(setMeta);
    load().catch((e) => toast.error(e.message));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTags = (k, v) => set(k, v.split(",").map((s) => s.trim()).filter(Boolean));

  const submit = async () => {
    if (!form.name || !form.url) return toast.error("Name and normal URL are required");
    try {
      const body = { ...form, affiliate_url: form.affiliate_url || null };
      if (editing) await api.put(`/admin/resources/${editing}`, body);
      else await api.post("/admin/resources", body);
      toast.success(editing ? "Resource updated" : "Resource added");
      setForm(BLANK);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const edit = (r) => {
    setEditing(r.resource_id);
    setForm({ ...BLANK, ...r, affiliate_url: r.affiliate_url || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    await api.del(`/admin/resources/${id}`);
    toast.success("Resource deleted");
    load();
  };

  const toggleActive = async (r) => {
    await api.put(`/admin/resources/${r.resource_id}`, { ...r, active: !r.active });
    load();
  };

  const addConversion = async () => {
    await api.post("/admin/conversions", { ...conv, amount: parseFloat(conv.amount || 0) });
    toast.success("Conversion logged");
    setConv({ resource_id: "", affiliate_network: "amazon", amount: "", note: "" });
    load();
  };

  if (meta && !meta.is_admin)
    return (
      <div className="panel p-12 max-w-xl" data-testid="admin-denied">
        <p className="overline">Restricted</p>
        <h1 className="mt-5 font-display text-3xl tracking-tight leading-none">Admin only</h1>
        <p className="mt-5 text-sm text-muted-foreground">This account can't manage the resource library.</p>
      </div>
    );

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="rise">
        <p className="overline">Affiliate control</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-none">Resource library</h1>
        <p className="mt-5 text-base text-muted-foreground max-w-2xl leading-relaxed">
          Loop recommends from this library only, matched to each user's stage. Commission never
          influences ranking.
        </p>
      </div>

      {stats && (
        <div className="grid md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="admin-analytics">
          {[
            ["Views", stats.total_views],
            ["Clicks", stats.total_clicks],
            ["CTR", `${stats.ctr}%`],
            ["Conversions", stats.conversions_count],
            ["Logged earnings", `$${stats.conversions_total}`],
          ].map(([k, v]) => (
            <div key={k} className="panel p-6">
              <span className="overline">{k}</span>
              <p className="mt-4 font-display text-3xl tracking-tight">{v}</p>
            </div>
          ))}
        </div>
      )}

      <div className="panel p-8" data-testid="resource-form">
        <div className="flex items-center gap-3">
          <span className="overline">{editing ? "Edit resource" : "Add resource"}</span>
          {editing && (
            <button onClick={() => { setEditing(null); setForm(BLANK); }} data-testid="cancel-edit-button"
              className="text-xs text-muted-foreground hover:text-white transition-colors duration-200 flex items-center gap-1">
              <X className="h-3 w-3" /> cancel
            </button>
          )}
        </div>
        <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          <Field label="Resource name">
            <input className={input} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="res-name" />
          </Field>
          <Field label="Category">
            <input className={input} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="res-category" />
          </Field>
          <Field label="Resource type">
            <select className={input} value={form.resource_type} onChange={(e) => set("resource_type", e.target.value)} data-testid="res-type">
              {(meta?.resource_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Normal URL">
            <input className={input} value={form.url} onChange={(e) => set("url", e.target.value)} data-testid="res-url" />
          </Field>
          <Field label="Affiliate URL (optional)">
            <input className={input} value={form.affiliate_url} onChange={(e) => set("affiliate_url", e.target.value)} data-testid="res-affiliate-url" />
          </Field>
          <Field label="Affiliate network">
            <select className={input} value={form.affiliate_network} onChange={(e) => set("affiliate_network", e.target.value)} data-testid="res-network">
              {(meta?.networks || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Affiliate program">
            <input className={input} value={form.affiliate_program} onChange={(e) => set("affiliate_program", e.target.value)} data-testid="res-program" />
          </Field>
          <Field label="Image / logo URL">
            <input className={input} value={form.image_url || ""} onChange={(e) => set("image_url", e.target.value)} data-testid="res-image" />
          </Field>
          <Field label="Price">
            <input className={input} value={form.price || ""} onChange={(e) => set("price", e.target.value)} data-testid="res-price" />
          </Field>
          <Field label="Goal tags (comma separated)">
            <input className={input} value={form.goal_tags.join(", ")} onChange={(e) => setTags("goal_tags", e.target.value)} data-testid="res-goal-tags" />
          </Field>
          <Field label="Roadmap stage tags (comma separated)">
            <input className={input} value={form.stage_tags.join(", ")} onChange={(e) => setTags("stage_tags", e.target.value)} data-testid="res-stage-tags" />
          </Field>
          <Field label="Domains (comma separated)">
            <input className={input} value={form.domains.join(", ")} onChange={(e) => setTags("domains", e.target.value)} data-testid="res-domains" />
          </Field>
          <Field label="Description">
            <textarea rows={2} className={input} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="res-description" />
          </Field>
          <Field label="Private notes (never shown to users)">
            <textarea rows={2} className={input} value={form.admin_notes || ""} onChange={(e) => set("admin_notes", e.target.value)} data-testid="res-notes" />
          </Field>
          <Field label="Commission note (private)">
            <input className={input} value={form.commission_note || ""} onChange={(e) => set("commission_note", e.target.value)} data-testid="res-commission" />
          </Field>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="res-active" />
            Active
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.sponsored} onChange={(e) => set("sponsored", e.target.checked)} data-testid="res-sponsored" />
            Sponsored / partner placement
          </label>
          {form.sponsored && (
            <select className={`${input} max-w-[220px]`} value={form.sponsor_label || "Sponsored"}
              onChange={(e) => set("sponsor_label", e.target.value)} data-testid="res-sponsor-label">
              {(meta?.sponsor_labels || []).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          <button onClick={submit} data-testid="save-resource-button"
            className="ml-auto flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors duration-200">
            {editing ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {editing ? "Save changes" : "Add resource"}
          </button>
        </div>
      </div>

      <div className="panel p-8" data-testid="resource-list">
        <span className="overline">{resources.length} resources</span>
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                {["Name", "Type", "Network", "Affiliate link", "Views", "Clicks", "Active", ""].map((h) => (
                  <th key={h} className="py-2 pr-4 font-normal text-xs uppercase tracking-[0.14em]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resources.map((r) => {
                const row = stats?.top_resources?.find((x) => x.resource_id === r.resource_id);
                return (
                  <tr key={r.resource_id} className="border-t border-white/[0.06]" data-testid={`row-${r.resource_id}`}>
                    <td className="py-3 pr-4">{r.name}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{r.resource_type}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{r.affiliate_network || "none"}</td>
                    <td className="py-3 pr-4">
                      {r.affiliate_url ? (
                        <span className="text-primary text-xs">set</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">using normal URL</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground">{row?.views || 0}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{row?.clicks || 0}</td>
                    <td className="py-3 pr-4">
                      <button onClick={() => toggleActive(r)} data-testid={`toggle-${r.resource_id}`}
                        className={`text-xs px-3 py-1 rounded-full border transition-colors duration-200 ${
                          r.active ? "border-primary/40 text-primary" : "border-white/12 text-muted-foreground"
                        }`}>
                        {r.active ? "active" : "inactive"}
                      </button>
                    </td>
                    <td className="py-3 flex gap-2">
                      <button onClick={() => edit(r)} data-testid={`edit-${r.resource_id}`}
                        className="text-xs px-3 py-1 rounded-full border border-white/12 text-muted-foreground hover:text-white transition-colors duration-200">
                        edit
                      </button>
                      <button onClick={() => remove(r.resource_id)} data-testid={`delete-${r.resource_id}`}
                        aria-label="Delete resource"
                        className="p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-200">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="panel p-8" data-testid="performance-panel">
          <span className="overline">Where clicks come from</span>
          <div className="mt-5 grid sm:grid-cols-2 gap-8">
            <div>
              <p className="text-xs text-muted-foreground mb-3">Categories</p>
              {(stats?.top_categories || []).map((c) => (
                <p key={c.key} className="text-sm py-1">{c.key} · <span className="text-muted-foreground">{c.count}</span></p>
              ))}
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-3">Roadmap stages</p>
              {(stats?.top_stages || []).map((c) => (
                <p key={c.key} className="text-sm py-1">{c.key} · <span className="text-muted-foreground">{c.count}</span></p>
              ))}
            </div>
          </div>
        </div>

        <div className="panel p-8" data-testid="conversion-panel">
          <span className="overline">Log a conversion manually</span>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
            Networks report sales on their own dashboards. Enter them here to see earnings next to clicks.
          </p>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <select className={input} value={conv.resource_id} onChange={(e) => setConv({ ...conv, resource_id: e.target.value })} data-testid="conv-resource">
              <option value="">Any resource</option>
              {resources.map((r) => <option key={r.resource_id} value={r.resource_id}>{r.name}</option>)}
            </select>
            <select className={input} value={conv.affiliate_network} onChange={(e) => setConv({ ...conv, affiliate_network: e.target.value })} data-testid="conv-network">
              {(meta?.networks || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <input className={input} placeholder="Amount, e.g. 18.50" value={conv.amount}
              onChange={(e) => setConv({ ...conv, amount: e.target.value })} data-testid="conv-amount" />
            <input className={input} placeholder="Note" value={conv.note}
              onChange={(e) => setConv({ ...conv, note: e.target.value })} data-testid="conv-note" />
          </div>
          <button onClick={addConversion} data-testid="add-conversion-button"
            className="mt-5 px-6 py-3 rounded-full border border-white/12 text-sm hover:bg-white/[0.06] transition-colors duration-200">
            Log conversion
          </button>
        </div>
      </div>
    </div>
  );
}
